import type { ReactNode } from 'react';

import { cn } from 'cn';

import { Tooltip, Unit, VirtualizedListServerDriven } from '@vestigas/ui';

type BarChartItem = {
  label: string;
  tooltipContent?: string;
  value: number;
};

type PageData = {
  data: BarChartItem[];
  hasNextPage: boolean;
  totalCount: number;
};

type P = {
  readonly colors?: readonly string[];
  readonly fetchPage: (
    offset: number,
    signal: AbortSignal,
  ) => Promise<PageData>;
  readonly layout?: 'vertical' | 'horizontal';
  readonly onClick?: (label: string, value: number) => void;
  readonly onLoadingChange?: (isLoading: boolean) => void;
  readonly pageSize?: number;
  readonly queryKey: readonly unknown[];
  readonly renderLoadingItem?: (index: number) => ReactNode;
  readonly totalCount?: number;
} & ComponentStyling;

const BAR_COLORS = ['#95e5b7', '#c5c9d6'];
const FILLER_BAR_COLOR = '#e4e6ed';
const SKELETON_COLOR = FILLER_BAR_COLOR;

const PercentageBarVerticalSkeleton = () => {
  return (
    <div className="flex h-10 w-full flex-col items-start gap-0 overflow-x-hidden pr-4 pl-px">
      <label className="line-clamp-1 w-full min-w-0 flex-none pl-1 text-xs">
        <div
          className="mb-1 h-3 w-24 animate-pulse rounded-xs brightness-[97%]"
          style={{ backgroundColor: SKELETON_COLOR }}
        />
      </label>
      <div className="flex h-4 w-full divide-x divide-white">
        <div
          className="h-full animate-pulse brightness-[97%]"
          style={{
            backgroundColor: SKELETON_COLOR,
            width: '80%',
          }}
        />
        <div
          className="h-full flex-1"
          style={{ backgroundColor: FILLER_BAR_COLOR }}
        />
      </div>
    </div>
  );
};

const PercentageBarVertical = ({
  className,
  colors = BAR_COLORS,
  label,
  onClick,
  style,
  tooltipContent,
  value,
}: {
  readonly colors?: readonly string[];
  readonly label: string;
  readonly onClick?: (label: string, value: number) => void;
  readonly tooltipContent?: string;
  readonly value: number;
} & ComponentStyling) => {
  return (
    <div
      className={cn(
        'relative flex h-10 w-full items-start pr-4 pl-px before:-top-1 before:bottom-1 before:-z-1 before:transition-opacity before:duration-300 before:ease-out hover:before:absolute hover:before:right-3 hover:before:-left-1 hover:before:rounded-sm hover:before:bg-tiga-action-lighter/13',
        className,
      )}
      style={style}
    >
      <div className="flex w-full min-w-0 flex-col items-start gap-0 overflow-x-hidden">
        <label className="line-clamp-1 w-full min-w-0 flex-none pl-1 text-xs">
          {label}
        </label>
        <div className="flex h-4 w-full divide-x divide-white">
          {value > 0 && (
            <Tooltip content={tooltipContent ?? `${value?.toFixed(0)} %`}>
              <div
                className={cn(
                  'h-full',
                  'hover:bg-[#a7c3ff]',
                  onClick ? 'cursor-pointer' : '',
                )}
                style={{
                  backgroundColor: colors[0] || '#888',
                  width: `${value}%`,
                }}
                onClick={() => onClick?.(label, value)}
              />
            </Tooltip>
          )}
          {value < 100 && (
            <div
              className="h-full flex-1"
              style={{
                backgroundColor: FILLER_BAR_COLOR,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const PercentageBar = ({
  className,
  colors = BAR_COLORS,
  label,
  onClick,
  style,
  tooltipContent,
  value,
}: {
  readonly colors?: readonly string[];
  readonly label: string;
  readonly onClick?: (label: string, value: number) => void;
  readonly tooltipContent?: string;
  readonly value: number;
} & ComponentStyling) => {
  return (
    <div
      className={cn(
        'relative flex h-7 w-full items-center pr-4 before:inset-y-0 before:-z-1 before:transition-opacity before:duration-300 before:ease-out hover:before:absolute hover:before:right-3 hover:before:left-0 hover:before:rounded-sm hover:before:bg-tiga-action-lighter/13',
        className,
      )}
      style={style}
    >
      <div className="flex h-5 w-full items-center gap-2">
        <label
          className="w-60 min-w-0 flex-none truncate pl-2 text-xs"
          title={label}
        >
          {label}
        </label>
        <div className="flex h-full flex-1 divide-x divide-white">
          {value > 0 && (
            <Tooltip content={tooltipContent ?? `${value?.toFixed(0)} %`}>
              <div
                className={cn(
                  'h-full',
                  'hover:bg-[#a7c3ff]',
                  onClick ? 'cursor-pointer' : '',
                )}
                style={{
                  backgroundColor: colors[0] || '#888',
                  width: `${value}%`,
                }}
                onClick={() => onClick?.(label, value)}
              />
            </Tooltip>
          )}
          {value < 100 && (
            <div
              className="h-full flex-1"
              style={{
                backgroundColor: colors[1] || '#888',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Draw grid lines in increments of 20%
const GRID_STEP_SIZE = 20 as const;

const ChartGrid = ({
  layout = 'horizontal',
}: {
  layout?: 'vertical' | 'horizontal';
}) => {
  return (
    <div className="absolute inset-0 mr-4 flex">
      {Array.from({
        length: 100 / Math.max(GRID_STEP_SIZE, 5) + 1,
      }).map((_, index: number) => (
        <div
          key={index}
          className={cn('relative mb-6 border-r border-tiga-border-lighter', {
            // Horizontal layout
            'w-62 flex-none border-[#c6c6c6]':
              index === 0 && layout === 'horizontal',
            'flex-1 border-[#dcdcdc]': index > 0 && layout === 'horizontal',

            // Vertical layout
            'w-0 flex-none border-tiga-border-default':
              index === 0 && layout === 'vertical',
            'flex-1 border-tiga-border-lighter':
              index > 0 && layout === 'vertical',
          })}
        >
          <div className="absolute right-0 -bottom-5 translate-x-1/2 text-xs text-tiga-text-default/70">
            <span>{index * Math.max(GRID_STEP_SIZE, 5)}</span>
            <Unit className="pl-[0.25ch]">%</Unit>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * InfiniteBarChart
 *
 * Displays a bar chart with windowed/sparse data fetching.
 * Only fetches pages for currently visible items, allowing users to jump
 * to any position without loading all intermediate data.
 */
export const InfiniteBarChart = ({
  className,
  colors = BAR_COLORS,
  fetchPage,
  layout = 'horizontal',
  onClick,
  onLoadingChange,
  pageSize = 100,
  queryKey,
  renderLoadingItem,
  style,
  totalCount,
}: P) => {
  return (
    <div className={cn('relative h-96', className)} style={style}>
      <VirtualizedListServerDriven<BarChartItem>
        key={layout}
        className="relative z-10 h-89"
        fetchPage={fetchPage}
        hasDivider={false}
        itemHeight={layout === 'vertical' ? 40 : 28}
        onLoadingChange={onLoadingChange}
        pageSize={pageSize}
        queryKey={queryKey}
        renderItem={(item: BarChartItem) => {
          return layout === 'vertical' ? (
            <PercentageBarVertical
              colors={colors}
              label={item.label}
              tooltipContent={item.tooltipContent}
              value={item.value}
              onClick={onClick}
            />
          ) : (
            <PercentageBar
              colors={colors}
              label={item.label}
              tooltipContent={item.tooltipContent}
              value={item.value}
              onClick={onClick}
            />
          );
        }}
        renderLoadingItem={
          renderLoadingItem ??
          (layout === 'vertical'
            ? () => <PercentageBarVerticalSkeleton />
            : undefined)
        }
        {...(totalCount !== undefined && { totalCount })}
      />

      <ChartGrid layout={layout} />
    </div>
  );
};
